import { AnimatePresence, motion } from "framer-motion";
import {
  Activity,
  BarChart3,
  Bot,
  ExternalLink,
  Landmark,
  MessageSquareText,
  Radio,
  Send,
  ShieldCheck,
  Users,
  WalletCards,
} from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { sendDashboardMessage } from "./lib/api";
import { summarizeAlertIngestOperatorLine } from "./lib/alertIngest";
import { summarizeDataHealthOperatorLine } from "./lib/dataHealth";
import { DataHealthPanel } from "./components/DataHealthPanel";
import { summarizeEventTruthTapeOperatorLine } from "./lib/eventTruthTape";
import { captureDashboardEvent, initDashboardAnalytics } from "./lib/analytics";
import { buildMeetingItems } from "./lib/conversation";
import { captureDashboardError } from "./lib/errorTracking";
import { selectFlatReadinessOverviewFact, summarizeFlatReadiness } from "./lib/flatReadiness";
import {
  compactAddress,
  formatDollar,
  formatKstTime,
  formatNumber,
  formatSignedUsdc,
  visibleStatus,
} from "./lib/format";
import { createDashboardLogger } from "./lib/logger";
import {
  buildDismissedRoster,
  summarizeRosterHealth,
  visibleAgentRoleFromMission,
} from "./lib/org";
import {
  buildPositionCards,
  buildTradeRows,
  filterTradeRows,
  sideMarkFromDirection,
  sideMarkFromSize,
  sideMarkLabel,
  summarizeTradeTape,
  tokenImageUrl,
  tradePnlClassName,
  type SideMark,
  type TradeTapeFilter,
} from "./lib/positions";
import { summarizeRuntimeStatusOperatorLine } from "./lib/runtimeStatus";
import { computeRoundTripStreakFromLedger } from "./lib/tradeStreak";
import { formatQualityLayerLabel } from "./lib/reviewReadiness";
import { summarizeEntrySubmissionFreezeBanner } from "./lib/entrySubmissionFreeze";
import { LEGACY_CYCLE_REQUIRED_TRADES } from "./lib/tradeCycle";
import type { EntryObservabilityResponse } from "./lib/types";
import { useDashboardData } from "./lib/useDashboardData";
import { useTradeCycleProgress } from "./lib/useTradeCycleProgress";

const dashboardLogger = createDashboardLogger("dashboard-app");

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.42 },
};

function MetricCard(props: {
  label: string;
  value: ReactNode;
  detail: ReactNode;
  tone?: "green" | "blue" | "amber" | "red";
  icon: ReactNode;
}) {
  return (
    <motion.section className={`metric metric-${props.tone ?? "blue"}`} {...fadeUp}>
      <div className="metric-icon">{props.icon}</div>
      <div className="metric-copy">
        <p>{props.label}</p>
        <strong>{props.value}</strong>
        <span>{props.detail}</span>
      </div>
    </motion.section>
  );
}

function summarizeEntryTempoOperatorLine(
  entry?: EntryObservabilityResponse,
  badge?: { headline: string; label: string; protocolVersion: string; detail?: string },
) {
  const progress = entry?.naturalTradeProgress;
  const day = entry?.entryTempo?.windows?.["24h"];
  if (!entry || !progress || !day) {
    return {
      tone: "amber" as const,
      title: "진입 템포",
      counts: "관측 대기",
      review: "거래 빈도 데이터를 불러오는 중",
    };
  }
  const warningCount = entry.entryTempo?.frequencyWarnings.length ?? 0;
  const warnings = new Set(entry.entryTempo?.frequencyWarnings ?? []);
  const warningLabel = warnings.has("filled_entry_flow_low_24h")
    ? "체결 빈도 낮음"
    : warnings.has("entry_candidate_flow_low_24h")
      ? "후보 빈도 낮음"
      : `빈도 경고 ${warningCount}개`;
  return {
    tone: warningCount
      ? ("amber" as const)
      : progress.complete
        ? ("green" as const)
        : ("blue" as const),
    title:
      badge?.protocolVersion === "profit-v1"
        ? `${badge.label} ${badge.headline}`
        : `거래 ${progress.actualTrades}/${progress.requiredTrades}`,
    counts: `24h 후보 ${day.entryNotes} · 24h 체결 ${day.uniqueFilledEntries}`,
    review: warningCount
      ? badge?.protocolVersion === "profit-v1"
        ? `${warningLabel} · ${badge.detail}`
        : `${warningLabel} · 관측 남은 ${progress.remainingTrades}`
      : `시간당 후보 ${day.entryNotesPerHour} · 24h 체결 ${day.uniqueFilledEntries}`,
  };
}

type AgentIdentity = {
  name?: string;
  gender?: "female" | "male";
  appearance?: {
    hair?: "short" | "bob" | "long";
    outfit?: "jacket" | "suit" | "hoodie" | "vest";
    accessory?: "none" | "glasses";
  };
};

type OrgAgent = {
  id: string;
  mission: string;
  toolAccess?: string[];
  identity?: AgentIdentity;
  status?: string;
  createdBy?: string;
  createdAt?: string;
  lastActivityAt?: string;
  redirections?: Array<{
    redirectedAt: string;
    redirectedBy: string;
    previousMission: string;
    mission: string;
    toolAccess: string[];
    reason: string;
  }>;
  terminatedReason?: string;
  reports?: Array<{
    reportedAt: string;
  }>;
};

type OrgHistoryEvent = {
  id: string;
  observedAt: string;
  action: "고용" | "수정" | "해고" | "권한";
  target: string;
  detail: string;
};

function visibleOrgAction(eventType: string): OrgHistoryEvent["action"] {
  if (eventType === "subagent_hired") return "고용";
  if (eventType === "subagent_terminated") return "해고";
  if (eventType.startsWith("execution_delegate")) return "권한";
  return "수정";
}

function visibleOrgEventDetail(eventType: string, fallback?: string) {
  if (eventType === "subagent_hired") return fallback || "새 직원";
  if (eventType === "subagent_terminated") return "업무 종료";
  if (eventType === "execution_delegate_assigned") return "실행권한 부여";
  if (eventType === "execution_delegate_revoked") return "실행권한 회수";
  if (eventType === "subagent_redirected") return "임무 조정";
  return "상태 수정";
}

function PixelPerson(props: {
  active?: boolean;
  delegate?: boolean;
  identity?: AgentIdentity;
  index: number;
}) {
  const variant = props.index % 4;
  const gender = props.identity?.gender ?? (variant % 2 === 0 ? "female" : "male");
  const hair = props.identity?.appearance?.hair ?? (gender === "female" ? "bob" : "short");
  const outfits = ["jacket", "suit", "hoodie", "vest"] as const;
  const outfit = props.identity?.appearance?.outfit ?? outfits[variant];
  const accessory = props.identity?.appearance?.accessory ?? "none";
  return (
    <span
      className={`pixel-person pixel-variant-${variant} pixel-${gender} hair-${hair} outfit-${outfit} accessory-${accessory} ${props.active ? "pixel-active" : ""} ${props.delegate ? "pixel-delegate" : ""}`}
      aria-hidden="true"
    >
      <span className="pixel-hair" />
      <span className="pixel-head" />
      <span className="pixel-glasses" />
      <span className="pixel-body" />
      <span className="pixel-feet" />
    </span>
  );
}

function visibleAgentRole(agent: OrgAgent, isDelegate: boolean) {
  return visibleAgentRoleFromMission(agent.mission, isDelegate);
}

function visibleMission(agent: OrgAgent) {
  const mission = agent.mission.toLowerCase();
  if (mission.includes("market") || mission.includes("rumor") || mission.includes("source"))
    return "시세·소식 감시";
  if (mission.includes("execution") || mission.includes("ledger") || mission.includes("audit"))
    return "체결·수수료 검토";
  if (mission.includes("risk")) return "위험 변화 검토";
  if (mission.includes("research")) return "새 기회 조사";
  return "CEO 지정 임무";
}

function visibleAgentName(agent: OrgAgent) {
  return agent.identity?.name?.trim() || visibleAgentRole(agent, false);
}

function visibleDetailedWorkItems(agent: OrgAgent, isDelegate: boolean) {
  const mission = agent.mission.toLowerCase();
  if (mission.includes("market") || mission.includes("rumor") || mission.includes("source")) {
    return ["시세 변화와 X 소식을 확인", "출처 신뢰도 메모 관리", "대표에게 시장 분위기 요약"];
  }
  if (mission.includes("execution") || mission.includes("ledger") || mission.includes("audit")) {
    return isDelegate
      ? ["실거래 실행권한 보유", "체결 기록과 수수료 확인", "반복 실패와 손익 기록 점검"]
      : ["체결 기록과 수수료 확인", "반복 실패와 손익 기록 점검", "실행 품질 개선점 정리"];
  }
  if (mission.includes("risk"))
    return ["위험 변화 확인", "포지션 노출 점검", "과도한 쏠림을 대표에게 보고"];
  if (mission.includes("research"))
    return ["새 거래 기회 조사", "근거 자료 정리", "대표에게 제안 제출"];
  return isDelegate
    ? ["대표가 맡긴 임무 수행", "필요 시 실거래 실행", "결과와 이유 기록"]
    : ["대표가 맡긴 임무 수행", "필요한 결과 정리", "팀에 보고"];
}

function chatTime(value: string) {
  return formatKstTime(value, "");
}

function knownAgentStartTime(agent: OrgAgent) {
  const candidates = [
    agent.createdAt,
    ...(agent.reports ?? []).map((report) => report.reportedAt),
    ...(agent.redirections ?? []).map((redirection) => redirection.redirectedAt),
    agent.lastActivityAt,
  ].filter((value): value is string => Boolean(value));
  return candidates.sort((left, right) => new Date(left).getTime() - new Date(right).getTime())[0];
}

function buildOrgHistory(
  agents: OrgAgent[],
  delegate:
    | { executionDelegate: { subagentId: string; assignedAt: string; reason: string } | null }
    | undefined,
  companyEvents?: Array<{
    observedAt: string;
    eventType: string;
    targetId?: string;
    targetName?: string;
    summary?: string;
    reason?: string;
  }>,
): OrgHistoryEvent[] {
  if (companyEvents?.length) {
    return companyEvents
      .filter((event) => !Number.isNaN(new Date(event.observedAt).getTime()))
      .map((event) => {
        const action = visibleOrgAction(event.eventType);
        return {
          id: `${event.eventType}-${event.targetId ?? event.targetName ?? "company"}-${event.observedAt}`,
          observedAt: event.observedAt,
          action,
          target:
            event.targetName ??
            agents.find((agent) => agent.id === event.targetId)?.identity?.name ??
            "회사",
          detail: visibleOrgEventDetail(
            event.eventType,
            action === "고용" ? event.summary : event.reason,
          ),
        };
      })
      .slice(0, 6);
  }

  const events: OrgHistoryEvent[] = [];

  for (const agent of agents) {
    const name = visibleAgentName(agent);
    const startedAt = knownAgentStartTime(agent);
    if (startedAt && /ceo/i.test(agent.createdBy ?? "")) {
      events.push({
        id: `${agent.id}-created-${startedAt}`,
        observedAt: startedAt,
        action: "고용",
        target: name,
        detail: visibleMission(agent),
      });
    }

    for (const redirection of agent.redirections ?? []) {
      events.push({
        id: `${agent.id}-redirected-${redirection.redirectedAt}`,
        observedAt: redirection.redirectedAt,
        action: "수정",
        target: name,
        detail: "임무 조정",
      });
    }

    if (agent.status === "terminated" && agent.lastActivityAt) {
      events.push({
        id: `${agent.id}-terminated-${agent.lastActivityAt}`,
        observedAt: agent.lastActivityAt,
        action: "해고",
        target: name,
        detail: "업무 종료",
      });
    }
  }

  const currentDelegate = delegate?.executionDelegate;
  if (currentDelegate) {
    const agent = agents.find((entry) => entry.id === currentDelegate.subagentId);
    events.push({
      id: `delegate-${currentDelegate.subagentId}-${currentDelegate.assignedAt}`,
      observedAt: currentDelegate.assignedAt,
      action: "권한",
      target: agent ? visibleAgentName(agent) : "실행 담당",
      detail: "실행권한 부여",
    });
  }

  return events
    .filter((event) => !Number.isNaN(new Date(event.observedAt).getTime()))
    .sort(
      (left, right) => new Date(right.observedAt).getTime() - new Date(left.observedAt).getTime(),
    )
    .slice(0, 6);
}

function speakerTone(name: string) {
  const tones = ["mint", "blue", "plum", "amber", "rose"] as const;
  if (name.includes("CEO")) return "rose";
  if (name === "나") return "blue";
  return tones[(name.trim().charCodeAt(0) || 0) % tones.length];
}

function speakerAvatarIndex(name: string) {
  let hash = 0;
  for (const character of name) hash += character.charCodeAt(0);
  return (hash % 4) + 1;
}

function speakerLabel(name: string, _role: string) {
  return name;
}

function renderMessageLine(line: string) {
  const trimmed = line.trim();
  const parts = trimmed
    .split(/\s+\|\s+/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length > 1) {
    return (
      <span className="message-line-parts">
        {parts.map((part, index) => (
          <span key={`${index}-${part}`}>{renderMessageLine(part)}</span>
        ))}
      </span>
    );
  }
  if (trimmed.startsWith("▲"))
    return (
      <>
        <span className="triangle-up">▲</span>
        {trimmed.slice(1)}
      </>
    );
  if (trimmed.startsWith("▼"))
    return (
      <>
        <span className="triangle-down">▼</span>
        {trimmed.slice(1)}
      </>
    );
  return line;
}

function openExternalSource(url: string) {
  const nextWindow = window.open(url, "_blank", "noopener,noreferrer");
  if (nextWindow) {
    nextWindow.opener = null;
    return;
  }
  window.location.href = url;
}

function TokenMark(props: {
  symbol: string;
  imageUrl: string;
  compact?: boolean;
  sideMark?: SideMark;
  sideMarkDimmed?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  const mark = props.sideMark;
  return (
    <span className={props.compact ? "token-mark-wrap token-mark-wrap-compact" : "token-mark-wrap"}>
      <span className={props.compact ? "token-mark token-mark-compact" : "token-mark"}>
        {!failed ? (
          props.imageUrl ? (
            <img
              src={props.imageUrl}
              alt=""
              loading="lazy"
              onError={() => setFailed(true)}
              aria-hidden="true"
            />
          ) : (
            <span aria-hidden="true">{props.symbol.slice(0, 2)}</span>
          )
        ) : (
          <span aria-hidden="true">{props.symbol.slice(0, 2)}</span>
        )}
      </span>
      {mark ? (
        <span
          className={`side-mark side-mark-${mark}${props.sideMarkDimmed ? " side-mark-dimmed" : ""}`}
          aria-label={mark === "long" ? "롱" : "숏"}
          title={mark === "long" ? "Long" : "Short"}
        >
          {sideMarkLabel(mark)}
        </span>
      ) : null}
    </span>
  );
}

export function App() {
  const { data, loading, error, fromCache } = useDashboardData();
  const [message, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState<
    "overview" | "collection" | "positions" | "org" | "activity" | "account"
  >("overview");
  const [expandedTradeId, setExpandedTradeId] = useState<string | undefined>();
  const [tradeTapeFilter, setTradeTapeFilter] = useState<TradeTapeFilter>("trades");
  const [expandedAgentId, setExpandedAgentId] = useState<string | undefined>();
  const [clockNow, setClockNow] = useState(() => new Date());

  const portfolio = data.portfolio;
  const ledger = data.ledger;
  const positions = portfolio?.userState?.assetPositions ?? [];
  const openOrders = portfolio?.openOrders ?? [];
  const accountValue = portfolio?.userState?.marginSummary?.accountValue;
  const withdrawable = portfolio?.userState?.withdrawable;
  const liveTrading = data.health?.config.liveTrading;
  const allTeam = data.subagents?.subagents ?? [];
  const activeTeam = allTeam.filter((agent) => agent.status === "active");
  const executionDelegateId = data.executionDelegate?.executionDelegate?.subagentId;
  const executionDelegateAgent = activeTeam.find((agent) => agent.id === executionDelegateId);
  const recentExecutions = ledger?.recentExecutions ?? [];
  const ledgerReady = Boolean(ledger);
  const grossPnl = ledger?.totalClosedPnl ?? 0;
  const totalFees = ledger?.totalFees ?? 0;
  const netPnl = grossPnl - totalFees;
  const teamCount = Math.max(activeTeam.length, 1);

  // Build a lightweight conversation fingerprint so buildMeetingItems only
  // re-runs when relevant data actually changes, not on every new object ref.
  const conversationFingerprint = useMemo(() => {
    const lastMsg = data.teamMessages?.messages?.[0]?.id ?? "";
    const lastCollab = data.collaborationNotes?.notes?.[0]?.id ?? "";
    const lastAlert = data.alertCandidates?.alerts?.[0]?.id ?? "";
    const lastProposal = data.proposals?.proposals?.[0]?.id ?? "";
    const agentReports = data.subagents?.subagents
      ?.map((a) => a.reports?.at(-1)?.reportedAt ?? "")
      .join(",");
    return `${lastMsg}|${lastCollab}|${lastAlert}|${lastProposal}|${agentReports}`;
  }, [
    data.teamMessages?.messages?.[0]?.id,
    data.collaborationNotes?.notes?.[0]?.id,
    data.alertCandidates?.alerts?.[0]?.id,
    data.proposals?.proposals?.[0]?.id,
    data.subagents?.subagents?.map((a) => a.reports?.at(-1)?.reportedAt).join(","),
  ]);

  const conversationItems = useMemo(() => buildMeetingItems(data), [conversationFingerprint]);
  const orgHistoryEvents = useMemo(
    () => buildOrgHistory(allTeam, data.executionDelegate, data.companyEvents?.events),
    [allTeam, data.executionDelegate, data.companyEvents],
  );
  const dismissedRoster = useMemo(() => buildDismissedRoster(allTeam), [allTeam]);
  const rosterHealth = useMemo(
    () =>
      summarizeRosterHealth(allTeam, clockNow.getTime(), {
        executionDelegateId: data.executionDelegate?.executionDelegate?.subagentId,
        collaborationNotes: data.collaborationNotes?.notes,
      }),
    [allTeam, clockNow, data.executionDelegate, data.collaborationNotes],
  );
  const rosterReviewIds = useMemo(
    () =>
      new Set([
        ...rosterHealth.reviewIds.activeWithoutReports,
        ...rosterHealth.reviewIds.transientMissions,
      ]),
    [rosterHealth],
  );
  const positionCards = useMemo(
    () => buildPositionCards(portfolio, ledger, clockNow),
    [portfolio, ledger, clockNow],
  );
  const tradeRows = useMemo(() => buildTradeRows(ledger, portfolio), [ledger, portfolio]);
  const positionFreshnessLine = useMemo(() => {
    if (fromCache) return "포트폴리오 캐시 사용 중";
    const portfolioObservedMs = Date.parse(String(portfolio?.observedAt ?? ""));
    const ledgerObservedMs = Date.parse(String(ledger?.observedAt ?? ""));
    if (!Number.isFinite(portfolioObservedMs) || !Number.isFinite(ledgerObservedMs)) return null;
    const skewSeconds = Math.abs(portfolioObservedMs - ledgerObservedMs) / 1000;
    if (skewSeconds <= 30) return null;
    return `포트폴리오·원장 시각 차이 ${Math.round(skewSeconds)}초`;
  }, [fromCache, ledger?.observedAt, portfolio?.observedAt]);
  const tradeTapeSummary = useMemo(() => summarizeTradeTape(tradeRows), [tradeRows]);
  const visibleTradeRows = useMemo(
    () => filterTradeRows(tradeRows, tradeTapeFilter),
    [tradeRows, tradeTapeFilter],
  );
  const recentExecutionById = useMemo(
    () => new Map(recentExecutions.map((item) => [item.intentId, item])),
    [recentExecutions],
  );
  const alertIngestLine = useMemo(
    () =>
      summarizeAlertIngestOperatorLine(data.alertIngestHealth, {
        failed: Boolean(data.errors?.background?.alertIngestHealth),
      }),
    [data.alertIngestHealth, data.errors?.background?.alertIngestHealth],
  );
  const runtimeStatusLine = useMemo(
    () => summarizeRuntimeStatusOperatorLine(data.runtimeStatus),
    [data.runtimeStatus],
  );
  const tradeCycle = useTradeCycleProgress();
  const flatReadiness = useMemo(
    () =>
      summarizeFlatReadiness(
        { ...data, entryObservability: tradeCycle.entry ?? data.entryObservability },
        tradeCycle.cycle,
      ),
    [data, tradeCycle.cycle, tradeCycle.entry],
  );
  const flatReadinessOverviewFact = selectFlatReadinessOverviewFact(flatReadiness);
  const eventTruthTapeLine = useMemo(
    () =>
      summarizeEventTruthTapeOperatorLine(data.eventTruthTape, {
        failed: Boolean(data.errors?.background?.eventTruthTape),
      }),
    [data.eventTruthTape, data.errors?.background?.eventTruthTape],
  );
  const dataHealthLine = useMemo(
    () =>
      summarizeDataHealthOperatorLine(data.dataHealth, {
        failed: Boolean(data.errors?.background?.dataHealth),
      }),
    [data.dataHealth, data.errors?.background?.dataHealth],
  );
  const tradeStreak = useMemo(() => computeRoundTripStreakFromLedger(ledger), [ledger]);
  const tradeCycleBadge = tradeCycle.badge;
  const entryFreezeBanner = useMemo(
    () => summarizeEntrySubmissionFreezeBanner(tradeCycle.cycle),
    [tradeCycle.cycle],
  );
  const entryTempoLine = useMemo(
    () => summarizeEntryTempoOperatorLine(tradeCycle.entry, tradeCycleBadge),
    [tradeCycle.entry, tradeCycleBadge],
  );

  useEffect(() => {
    // Defer heavy analytics (PostHog/Sentry) to make the page appear faster on first load
    const timer = setTimeout(() => {
      initDashboardAnalytics();
      dashboardLogger.info("dashboard_loaded", {
        liveTrading: Boolean(liveTrading),
        activeTeamCount: activeTeam.length,
        hasErrors: Boolean(error),
      });
      captureDashboardEvent("dashboard_loaded", {
        liveTrading: Boolean(liveTrading),
      });
    }, 1200);

    return () => clearTimeout(timer);
  }, [activeTeam.length, error, liveTrading]);

  useEffect(() => {
    const timer = window.setInterval(() => setClockNow(new Date()), 15_000);
    return () => window.clearInterval(timer);
  }, []);

  const operationLabel = useMemo(() => {
    if (loading) return "확인 중";
    if (error && !fromCache) return "연결 확인";
    if (fromCache) return liveTrading ? `캐시 · 포지션 ${positions.length}` : "캐시 · 관찰 모드";
    return liveTrading ? "포지션 - " + positions.length : "관찰 모드";
  }, [error, fromCache, liveTrading, loading, positions.length]);

  const tabs = [
    ["overview", "홈"],
    ["collection", "수집·엣지"],
    ["positions", "포지션"],
    ["org", "조직도"],
    ["activity", "활동"],
    ["account", "자산"],
  ] as const;

  const branchTargets = Array.from(
    { length: teamCount },
    (_, index) => ((index + 0.5) / teamCount) * 100,
  );

  return (
    <main className="app">
      <header className="hero">
        <p className="brand-wordmark">HyperMax</p>
        <section
          className={`trade-cycle-badge trade-cycle-${tradeCycleBadge.tone}${tradeCycleBadge.loading ? " trade-cycle-loading" : ""}`}
          aria-label={`${tradeCycleBadge.label} ${tradeCycleBadge.actualTrades}건 중 ${tradeCycleBadge.requiredTrades}건`}
        >
          <div className="trade-cycle-header">
            <p className="trade-cycle-label">{tradeCycleBadge.label}</p>
            {tradeCycleBadge.reviewReadiness ? (
              <span
                className={`trade-cycle-status-pill trade-cycle-status-${tradeCycleBadge.reviewReadiness.status}`}
              >
                {tradeCycleBadge.reviewReadiness.statusLabelKo}
              </span>
            ) : null}
          </div>
          <strong className="trade-cycle-count">
            {tradeCycleBadge.headline}
            {tradeStreak ? (
              <span className={`trade-streak-chip trade-streak-${tradeStreak.type}`}>
                {tradeStreak.label}
              </span>
            ) : null}
          </strong>
          <div
            className="trade-cycle-bar"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={
              tradeCycleBadge.requiredTrades > 0
                ? tradeCycleBadge.requiredTrades
                : LEGACY_CYCLE_REQUIRED_TRADES
            }
            aria-valuenow={tradeCycleBadge.actualTrades}
            aria-label={`${tradeCycleBadge.label} fill 진행률`}
          >
            <span style={{ width: `${tradeCycleBadge.percent}%` }} />
          </div>
          {tradeCycleBadge.reviewReadiness ? (
            <div className="trade-cycle-layers">
              {tradeCycleBadge.readinessLayers ? (
                <span className="trade-cycle-layers-line">{tradeCycleBadge.readinessLayers}</span>
              ) : null}
              <div
                className="trade-cycle-quality-bar"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={tradeCycleBadge.reviewReadiness.qualityLayer.required ?? 40}
                aria-valuenow={tradeCycleBadge.reviewReadiness.qualityLayer.actual ?? 0}
                aria-label={`${formatQualityLayerLabel(tradeCycleBadge.reviewReadiness.qualityLayer) ?? "RT"} 품질층 진행률`}
              >
                <span
                  style={{
                    width: `${tradeCycleBadge.reviewReadiness.qualityLayer.percent ?? 0}%`,
                  }}
                />
              </div>
            </div>
          ) : null}
          <span className="trade-cycle-detail">{tradeCycleBadge.detail}</span>
          {tradeCycleBadge.meta ? (
            <span
              className={
                tradeCycle.stale ? "trade-cycle-meta trade-cycle-meta-stale" : "trade-cycle-meta"
              }
            >
              {tradeCycleBadge.meta}
            </span>
          ) : null}
          {tradeCycleBadge.nextAction ? (
            <div className="trade-cycle-next-action" aria-label="다음 개선 작업">
              <span className="trade-cycle-next-title">{tradeCycleBadge.nextAction.title}</span>
              <strong>{tradeCycleBadge.nextAction.primary}</strong>
              <span>{tradeCycleBadge.nextAction.gate}</span>
              <span>{tradeCycleBadge.nextAction.guardrail}</span>
            </div>
          ) : null}
        </section>
        {entryFreezeBanner ? (
          <div className="org-health-banner discipline-flat-banner" aria-label="진입 제출 동결">
            <strong>{entryFreezeBanner.title}</strong>
            <span>{entryFreezeBanner.detail}</span>
          </div>
        ) : null}
      </header>

      <nav className="main-tabs" aria-label="화면 선택">
        {tabs.map(([key, label]) => (
          <button
            key={key}
            type="button"
            aria-current={activeTab === key ? "page" : undefined}
            className={activeTab === key ? "main-tab main-tab-active" : "main-tab"}
            onClick={() => {
              setActiveTab(key);
              captureDashboardEvent("dashboard_tab_changed", { tab: key });
            }}
          >
            {label}
          </button>
        ))}
      </nav>

      <AnimatePresence mode="wait">
        {activeTab === "overview" && (
          <motion.section
            key="overview"
            className="tab-screen overview-screen"
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.24 }}
          >
            <section className="home-layout">
              <div className="home-main">
                <section className="metrics-grid" aria-label="핵심 상태">
                  <MetricCard
                    label="자산"
                    value={formatDollar(accountValue)}
                    detail={"출금 " + formatDollar(withdrawable)}
                    tone="green"
                    icon={<WalletCards size={20} />}
                  />
                  <MetricCard
                    label="자동 운용"
                    value={operationLabel}
                    detail={liveTrading ? "가능" : "대기"}
                    tone={error ? "amber" : liveTrading ? "blue" : "amber"}
                    icon={<Radio size={20} />}
                  />
                  <MetricCard
                    label="최근 실행"
                    value={ledgerReady ? recentExecutions.length + "건" : "확인 중"}
                    detail={
                      ledgerReady ? (
                        <span className={netPnl < 0 ? "loss-text" : "gain-text"}>
                          순손익 {formatSignedUsdc(netPnl)}
                        </span>
                      ) : (
                        "거래 내역 동기화 중"
                      )
                    }
                    tone={!ledgerReady ? "amber" : netPnl < 0 ? "red" : "green"}
                    icon={<Landmark size={20} />}
                  />
                  <MetricCard
                    label="팀원"
                    value={"- " + activeTeam.length + "명"}
                    detail={
                      rosterHealth.warningCount
                        ? `실행권한 ${executionDelegateAgent ? "1명" : "없음"} · 무보고 ${rosterHealth.activeWithoutReportsCount}명 · 렌즈 ${rosterHealth.activeStaffLensReadyCount}명`
                        : `실행권한 ${executionDelegateAgent ? "1명" : "없음"} · 전체 ${data.subagents?.subagents.length ?? 0}명`
                    }
                    tone={rosterHealth.warningCount ? "amber" : "blue"}
                    icon={<Users size={20} />}
                  />
                </section>
                <div className="status-strip-grid" aria-label="운용 상태 요약">
                  <section
                    className={`source-health-strip source-health-${alertIngestLine.tone}`}
                    aria-label="알림 소스 상태"
                  >
                    <ShieldCheck size={18} aria-hidden="true" />
                    <div>
                      <strong>{alertIngestLine.title}</strong>
                      <span>{alertIngestLine.counts}</span>
                    </div>
                    <p>{alertIngestLine.review}</p>
                  </section>
                  <section
                    className={`source-health-strip source-health-${runtimeStatusLine.tone}`}
                    aria-label="X 소식 감시 상태"
                  >
                    <Radio size={18} aria-hidden="true" />
                    <div>
                      <strong>{runtimeStatusLine.title}</strong>
                      <span>{runtimeStatusLine.counts}</span>
                    </div>
                    <p>{runtimeStatusLine.review}</p>
                  </section>
                  <section
                    className={`source-health-strip source-health-${flatReadiness.tone}`}
                    aria-label="포지션 공백 진단"
                  >
                    <Landmark size={18} aria-hidden="true" />
                    <div>
                      <strong>{flatReadiness.title}</strong>
                      <span>{flatReadiness.facts[0] ?? flatReadiness.detail}</span>
                    </div>
                    <p>{flatReadinessOverviewFact}</p>
                  </section>
                  <section
                    className={`source-health-strip source-health-${dataHealthLine.tone}`}
                    aria-label="데이터 수집 & 엣지 진전"
                  >
                    <BarChart3 size={18} aria-hidden="true" />
                    <div>
                      <strong>{dataHealthLine.title}</strong>
                      <span>{dataHealthLine.counts}</span>
                    </div>
                    <p>{dataHealthLine.review}</p>
                  </section>
                  <section
                    className={`source-health-strip source-health-${eventTruthTapeLine.tone}`}
                    aria-label="정보 엣지 측정 (Event Truth Tape)"
                  >
                    <Activity size={18} aria-hidden="true" />
                    <div>
                      <strong>{eventTruthTapeLine.title}</strong>
                      <span>{eventTruthTapeLine.counts}</span>
                    </div>
                    <p>{eventTruthTapeLine.review}</p>
                  </section>
                  <section
                    className={`source-health-strip source-health-${entryTempoLine.tone}`}
                    aria-label="진입 빈도 진단"
                  >
                    <Activity size={18} aria-hidden="true" />
                    <div>
                      <strong>{entryTempoLine.title}</strong>
                      <span>{entryTempoLine.counts}</span>
                    </div>
                    <p>{entryTempoLine.review}</p>
                  </section>
                </div>
                <section className="panel conversation home-chat">
                  <div className="panel-head">
                    <div>
                      <h2>회의실</h2>
                    </div>
                    <MessageSquareText size={22} />
                  </div>
                  <div className="chat-surface">
                    <div className="message-list" aria-label="회의 기록">
                      {conversationItems.length ? (
                        conversationItems.map((item) => {
                          const isOwner = item.speakerName === "나";
                          const isAutomated = item.speakerRole === "자동 감시";
                          const tone = speakerTone(item.speakerName);
                          return (
                            <article
                              className={isOwner ? "message-row message-row-self" : "message-row"}
                              key={item.id}
                            >
                              {!isOwner && (
                                <div
                                  className={
                                    isAutomated
                                      ? "message-avatar machine-avatar"
                                      : `message-avatar avatar-${tone}`
                                  }
                                  aria-hidden="true"
                                >
                                  {isAutomated ? (
                                    <Bot size={19} />
                                  ) : (
                                    <PixelPerson
                                      active
                                      identity={item.speakerIdentity}
                                      index={speakerAvatarIndex(item.speakerName)}
                                    />
                                  )}
                                </div>
                              )}
                              <div className="message-stack">
                                <div className="message-meta">
                                  <strong className={`speaker-name speaker-${tone}`}>
                                    {speakerLabel(item.speakerName, item.speakerRole)}
                                  </strong>
                                  <time dateTime={item.observedAt}>
                                    {chatTime(item.observedAt)}
                                  </time>
                                </div>
                                <motion.article
                                  className={
                                    isOwner ? "message-item message-item-self" : "message-item"
                                  }
                                  initial={{ opacity: 0, y: 8 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ duration: 0.22 }}
                                >
                                  <div className="message-title-row">
                                    <h3>{item.title}</h3>
                                    {item.symbol ? (
                                      <span
                                        className="coin-badge"
                                        title={`${item.symbol} 관련 대화`}
                                      >
                                        <img
                                          src={tokenImageUrl(item.symbol)}
                                          alt={item.symbol}
                                          width={14}
                                          height={14}
                                          onError={(e) => {
                                            // Graceful fallback if external Hyperliquid coin logo fails
                                            (e.target as HTMLImageElement).style.display = "none";
                                          }}
                                        />
                                        {item.symbol}
                                      </span>
                                    ) : null}
                                  </div>
                                  {item.targetNames?.length ? (
                                    <p className="message-target">
                                      함께 보는 직원: {item.targetNames.join(", ")}
                                    </p>
                                  ) : null}
                                  <p className="message-summary">
                                    {renderMessageLine(item.summary)}
                                  </p>
                                  {item.chips?.length ? (
                                    <div className="message-chips" aria-label="요약 태그">
                                      {item.chips.map((chip) => (
                                        <span
                                          key={`${item.id}-${chip.tone}-${chip.label}`}
                                          className={`message-chip chip-${chip.tone}`}
                                        >
                                          {chip.label}
                                        </span>
                                      ))}
                                    </div>
                                  ) : null}
                                  {item.evidenceLine ? (
                                    <p className="message-evidence">
                                      {renderMessageLine(item.evidenceLine)}
                                    </p>
                                  ) : null}
                                  <ul>
                                    {item.bodyLines
                                      .filter(
                                        (line) =>
                                          line !== item.summary && line !== item.evidenceLine,
                                      )
                                      .map((line) => (
                                        <li key={line}>{renderMessageLine(line)}</li>
                                      ))}
                                  </ul>
                                  {item.sourceLinks?.length ? (
                                    <div className="message-sources" aria-label="소식 출처">
                                      {item.sourceLinks.map((source) => (
                                        <a
                                          key={`${item.id}-${source.url}`}
                                          href={source.url}
                                          target="_blank"
                                          rel="noreferrer"
                                          title={`${source.label} 새창에서 열기`}
                                          onClick={(event) => {
                                            event.preventDefault();
                                            openExternalSource(source.url);
                                          }}
                                        >
                                          {source.label}
                                          <ExternalLink size={11} aria-hidden="true" />
                                        </a>
                                      ))}
                                    </div>
                                  ) : null}
                                </motion.article>
                              </div>
                            </article>
                          );
                        })
                      ) : (
                        <div className="assistant-bubble">
                          <strong>회의 기록 대기</strong>
                          <p>새 회의 기록이 생기면 이곳에 표시됩니다.</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <form
                    className="composer"
                    onSubmit={async (event) => {
                      event.preventDefault();
                      const trimmed = message.trim();
                      if (!trimmed) return;
                      await sendDashboardMessage(trimmed).catch((sendError: unknown) => {
                        dashboardLogger.warn("owner_message_send_failed", {
                          bodyLength: trimmed.length,
                        });
                        captureDashboardError(sendError, { action: "owner_message_submit" });
                      });
                      captureDashboardEvent("owner_message_submitted", {
                        bodyLength: trimmed.length,
                      });
                      setMessage("");
                    }}
                  >
                    <input
                      value={message}
                      onChange={(event) => setMessage(event.target.value)}
                      placeholder="대표에게 보낼 말을 입력하세요"
                      aria-label="대표에게 보낼 말"
                    />
                    <button type="submit" disabled={!message.trim()} title="회의실에 남기기">
                      <Send size={18} />
                      <span>보내기</span>
                    </button>
                  </form>
                </section>
              </div>
            </section>
          </motion.section>
        )}

        {activeTab === "collection" && (
          <DataHealthPanel
            response={data.dataHealth}
            failed={Boolean(data.errors?.background?.dataHealth)}
          />
        )}

        {activeTab === "positions" && (
          <motion.section
            key="positions"
            className="tab-screen"
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.24 }}
          >
            <section className="panel solo-panel positions-panel">
              <div className="panel-head">
                <div>
                  <p className="eyebrow">열린 포지션</p>
                  <h2>포지션</h2>
                  {positionFreshnessLine ? (
                    <p className="muted-text position-freshness">{positionFreshnessLine}</p>
                  ) : null}
                </div>
                <Activity size={21} />
              </div>
              <div className="position-board" aria-label="열린 포지션">
                {positionCards.length ? (
                  positionCards.map((position) => (
                    <article
                      className={`position-card position-${position.direction}`}
                      key={position.symbol}
                    >
                      <div className="position-card-top">
                        <TokenMark
                          symbol={position.symbol}
                          imageUrl={position.imageUrl}
                          sideMark={sideMarkFromDirection(position.direction)}
                        />
                        <div>
                          <strong>{position.symbol}</strong>
                          <span
                            className={`position-side-pill position-side-pill-${position.direction}`}
                          >
                            {position.direction === "short" ? "Short" : "Long"}
                          </span>
                        </div>
                        <em className={position.pnlTone === "loss" ? "loss-text" : "gain-text"}>
                          {position.pnlLabel}
                        </em>
                      </div>
                      {position.entryReason ? (
                        <p className="position-entry-reason">{position.entryReason}</p>
                      ) : null}
                      <div className="position-card-grid">
                        <span>
                          <b>수량</b>
                          {position.sizeLabel}
                        </span>
                        <span>
                          <b>진입</b>
                          {position.entryLabel}
                        </span>
                        <span>
                          <b>가치</b>
                          {position.valueLabel}
                        </span>
                        <span>
                          <b>ROE</b>
                          {position.roeLabel}
                        </span>
                        <span>
                          <b>레버</b>
                          {position.leverageLabel}
                        </span>
                        <span>
                          <b>증거금</b>
                          {position.marginLabel}
                        </span>
                        {position.liquidationLabel ? (
                          <span>
                            <b>청산가</b>
                            {position.liquidationLabel}
                          </span>
                        ) : null}
                      </div>
                      <div className="position-opened">
                        <span>오픈 {position.openedAtLabel}</span>
                        <strong>{position.elapsedLabel}</strong>
                      </div>
                    </article>
                  ))
                ) : (
                  <p className="empty">현재 열린 포지션이 없습니다.</p>
                )}
              </div>
              <section className="trade-tape" aria-label="최근 거래 내역">
                <div className="trade-tape-head">
                  <div>
                    <strong>거래 내역</strong>
                    <span className="trade-tape-sub">
                      {ledgerReady
                        ? `체결·청산 ${tradeTapeSummary.trades} · 수수료 ${tradeTapeSummary.feeOnly}${
                            (ledger?.statusCounts?.filled_unmatched_ws ?? 0) > 0
                              ? ` · WS 미매칭 ${ledger!.statusCounts.filled_unmatched_ws}`
                              : ""
                          }`
                        : "동기화 중"}
                    </span>
                  </div>
                  <div className="trade-tape-filters" role="tablist" aria-label="거래 내역 필터">
                    {(
                      [
                        ["trades", "체결·청산"],
                        ["fees", "수수료"],
                        ["errors", "오류"],
                        ["all", "전체"],
                      ] as const
                    ).map(([id, label]) => (
                      <button
                        key={id}
                        type="button"
                        role="tab"
                        aria-selected={tradeTapeFilter === id}
                        className={tradeTapeFilter === id ? "active" : undefined}
                        onClick={() => setTradeTapeFilter(id)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="trade-tape-list">
                  {visibleTradeRows.length ? (
                    visibleTradeRows.map((trade) => (
                      <article className={`trade-line trade-line-${trade.kind}`} key={trade.id}>
                        <TokenMark
                          compact
                          symbol={trade.symbol}
                          imageUrl={trade.imageUrl}
                          sideMark={trade.sideMark}
                          sideMarkDimmed={
                            trade.kind === "exit" ||
                            trade.kind === "exit_reduce" ||
                            trade.kind === "exit_breakeven"
                          }
                        />
                        <div className="trade-line-main">
                          <strong>{trade.symbol}</strong>
                          <span className={`trade-badge trade-badge-${trade.badgeTone}`}>
                            {trade.badgeLabel}
                          </span>
                          <span className="trade-side">{trade.sideLabel}</span>
                        </div>
                        <div className="trade-line-pnl">
                          <em className={tradePnlClassName(trade.pnlTone)}>{trade.pnlLabel}</em>
                          {trade.feeLabel ? (
                            <small className="trade-fee">{trade.feeLabel}</small>
                          ) : null}
                          {trade.roundTripNetLabel ? (
                            <small className="trade-round-trip">
                              왕복 순손익: {trade.roundTripNetLabel}
                            </small>
                          ) : null}
                        </div>
                        <time>{trade.timeLabel}</time>
                      </article>
                    ))
                  ) : (
                    <p className="empty">
                      {ledgerReady
                        ? tradeTapeFilter === "fees"
                          ? "표시할 수수료 항목이 없습니다."
                          : tradeTapeFilter === "errors"
                            ? "오류·미체결 항목이 없습니다."
                            : "아직 표시할 체결·청산 내역이 없습니다."
                        : "거래 내역을 불러오는 중입니다."}
                    </p>
                  )}
                </div>
              </section>
            </section>
          </motion.section>
        )}

        {activeTab === "org" && (
          <motion.section
            key="org"
            className="tab-screen"
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.24 }}
          >
            <section className="panel org-panel solo-panel">
              <div className="panel-head org-head">
                <div>
                  <p className="eyebrow">회사</p>
                  <h2>조직도</h2>
                </div>
                <ShieldCheck size={21} />
              </div>
              {rosterHealth.warningCount > 0 && (
                <div className="org-health-banner" aria-label="직원 건강 점검">
                  <strong>직원 건강 점검</strong>
                  <span>
                    무보고 {rosterHealth.activeWithoutReportsCount}명 · 협업렌즈{" "}
                    {rosterHealth.activeStaffLensReadyCount}명 · 임시 임무{" "}
                    {rosterHealth.transientMissionCount}명
                  </span>
                </div>
              )}
              <div className="org-stage org-stage-wide">
                <article className="org-card ceo-card">
                  <PixelPerson
                    active
                    index={0}
                    identity={{
                      gender: "female",
                      appearance: { hair: "bob", outfit: "suit", accessory: "glasses" },
                    }}
                  />
                  <div>
                    <p>대표</p>
                    <strong>CEO</strong>
                    <span>조직과 권한을 정함</span>
                  </div>
                </article>
                <svg
                  className="org-tree-links"
                  viewBox="0 0 100 44"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  {branchTargets.map((target) => (
                    <path key={target} d={`M 50 0 C 50 16 ${target} 18 ${target} 44`} />
                  ))}
                </svg>
                <div className="team-row">
                  {activeTeam.map((agent, index) => {
                    const isDelegate = agent.id === executionDelegateId;
                    const isExpanded = expandedAgentId === agent.id;
                    const needsReview = rosterReviewIds.has(agent.id);
                    return (
                      <article className="team-branch" key={agent.id}>
                        <button
                          type="button"
                          className={`${isDelegate ? "org-card team-card delegate-card" : "org-card team-card"} ${needsReview ? "team-card-needs-review" : ""} org-tone-${speakerTone(visibleAgentName(agent))}`}
                          aria-expanded={isExpanded}
                          onClick={() => setExpandedAgentId(isExpanded ? undefined : agent.id)}
                        >
                          <PixelPerson
                            active
                            delegate={isDelegate}
                            identity={agent.identity}
                            index={speakerAvatarIndex(visibleAgentName(agent))}
                          />
                          <div className="agent-card-copy">
                            <div className="agent-card-topline">
                              <p>{visibleAgentRole(agent, isDelegate)}</p>
                              {(needsReview || isDelegate) && (
                                <span className="agent-card-badges">
                                  {needsReview && <span className="agent-health-badge">점검</span>}
                                  {isDelegate && (
                                    <span className="agent-authority-badge">
                                      <ShieldCheck size={12} aria-hidden="true" />
                                      실행권한
                                    </span>
                                  )}
                                </span>
                              )}
                            </div>
                            <strong
                              className={`speaker-name speaker-${speakerTone(visibleAgentName(agent))}`}
                            >
                              {visibleAgentName(agent)}
                            </strong>
                            <span>{visibleMission(agent)}</span>
                          </div>
                        </button>
                        <AnimatePresence initial={false}>
                          {isExpanded && (
                            <motion.div
                              className="agent-detail"
                              initial={{ opacity: 0, height: 0, y: -8 }}
                              animate={{ opacity: 1, height: "auto", y: 0 }}
                              exit={{ opacity: 0, height: 0, y: -8 }}
                              transition={{ duration: 0.24 }}
                            >
                              <strong>업무</strong>
                              <ul>
                                {visibleDetailedWorkItems(agent, isDelegate).map((item) => (
                                  <li key={item}>{item}</li>
                                ))}
                                {agent.identity?.stateNote && (
                                  <li>현재 상태: {agent.identity.stateNote}</li>
                                )}
                              </ul>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </article>
                    );
                  })}
                  {!activeTeam.length && (
                    <article className="team-branch">
                      <button type="button" className="org-card team-card empty-team">
                        <PixelPerson
                          index={1}
                          identity={{
                            gender: "male",
                            appearance: { hair: "short", outfit: "vest", accessory: "none" },
                          }}
                        />
                        <div>
                          <p>팀원</p>
                          <strong>대기</strong>
                          <span>CEO가 고용하면 여기에 표시됩니다</span>
                        </div>
                      </button>
                    </article>
                  )}
                </div>
              </div>
              <section className="org-history-card" aria-label="CEO 운영 이력">
                <div className="org-history-head">
                  <p>운영 이력</p>
                  <span>최근 {orgHistoryEvents.length}건</span>
                </div>
                {orgHistoryEvents.length ? (
                  <ol className="org-history-list">
                    {orgHistoryEvents.map((event) => (
                      <li key={event.id} className={`org-history-item event-${event.action}`}>
                        <time dateTime={event.observedAt}>{chatTime(event.observedAt)}</time>
                        <strong>{event.action}</strong>
                        <span>{event.target}</span>
                        <em>{event.detail}</em>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="org-history-empty">아직 표시할 운영 이력이 없습니다.</p>
                )}
              </section>
              <section className="detention-card" aria-label="수용소">
                <div className="detention-head">
                  <div>
                    <p>수용소</p>
                    <span>짤린 직원 집합</span>
                  </div>
                  <strong>{dismissedRoster.length}명</strong>
                </div>
                {dismissedRoster.length ? (
                  <div className="detention-grid">
                    {dismissedRoster.map((agent, index) => (
                      <article className="detention-agent" key={agent.id}>
                        <PixelPerson
                          identity={agent.identity}
                          index={speakerAvatarIndex(agent.name) + index}
                        />
                        <div>
                          <strong>{agent.name}</strong>
                          <span>{agent.role}</span>
                          <em>{agent.reason}</em>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <p className="detention-empty">아직 수용된 직원이 없습니다.</p>
                )}
              </section>
            </section>
          </motion.section>
        )}

        {activeTab === "activity" && (
          <motion.section
            key="activity"
            className="tab-screen"
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.24 }}
          >
            <section className="panel solo-panel">
              <div className="panel-head">
                <div>
                  <p className="eyebrow">활동</p>
                  <h2>거래와 근거</h2>
                </div>
                <Activity size={21} />
              </div>
              <div className="activity-tape-filters" role="tablist" aria-label="활동 필터">
                {(
                  [
                    ["trades", "체결·청산"],
                    ["fees", "수수료"],
                    ["errors", "오류"],
                    ["all", "전체"],
                  ] as const
                ).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={tradeTapeFilter === id}
                    className={tradeTapeFilter === id ? "active" : undefined}
                    onClick={() => setTradeTapeFilter(id)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="activity-list compact-list">
                {visibleTradeRows.map((trade) => {
                  const item = recentExecutionById.get(trade.id);
                  return (
                    <article className={`activity-card activity-${trade.kind}`} key={trade.id}>
                      <button
                        type="button"
                        className="activity-button"
                        onClick={() =>
                          setExpandedTradeId(expandedTradeId === trade.id ? undefined : trade.id)
                        }
                      >
                        <TokenMark
                          compact
                          symbol={trade.symbol}
                          imageUrl={trade.imageUrl}
                          sideMark={trade.sideMark}
                          sideMarkDimmed={
                            trade.kind === "exit" ||
                            trade.kind === "exit_reduce" ||
                            trade.kind === "exit_breakeven"
                          }
                        />
                        <strong>{item ? visibleStatus(item.status) : "기록"}</strong>
                        <span className="activity-button-meta">
                          <span className={`trade-badge trade-badge-${trade.badgeTone}`}>
                            {trade.badgeLabel}
                          </span>
                          {trade.sideLabel} {trade.symbol} {trade.sizeLabel}
                        </span>
                        <span className="activity-button-pnl">
                          <em className={tradePnlClassName(trade.pnlTone)}>{trade.pnlLabel}</em>
                          {trade.feeLabel ? (
                            <small className="trade-fee">{trade.feeLabel}</small>
                          ) : null}
                          {trade.roundTripNetLabel ? (
                            <small className="trade-round-trip">
                              왕복 순손익: {trade.roundTripNetLabel}
                            </small>
                          ) : null}
                        </span>
                      </button>
                      <AnimatePresence initial={false}>
                        {expandedTradeId === trade.id && item && (
                          <motion.div
                            className="activity-detail"
                            initial={{ opacity: 0, height: 0, y: -6 }}
                            animate={{ opacity: 1, height: "auto", y: 0 }}
                            exit={{ opacity: 0, height: 0, y: -6 }}
                            transition={{ duration: 0.24 }}
                          >
                            <p>
                              판단 이유:{" "}
                              {item.thesis || "이 거래에는 아직 별도 판단 메모가 없습니다."}
                            </p>
                            <p>
                              손익: 실현 {formatSignedUsdc(item.closedPnl)} · 수수료{" "}
                              {formatSignedUsdc(-Math.abs(item.fees ?? 0))} · 순손익{" "}
                              {formatSignedUsdc((item.closedPnl ?? 0) - (item.fees ?? 0))}
                            </p>
                            {item.noteId && (
                              <p className="note-ref">
                                연결된 협업 노트: <code>{item.noteId}</code>
                              </p>
                            )}
                            <p>근거 소식: 체결 기록에 연결된 원문이 아직 없습니다.</p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </article>
                  );
                })}
                {!visibleTradeRows.length && (
                  <p className="empty">
                    {ledgerReady
                      ? tradeTapeFilter === "fees"
                        ? "표시할 수수료 활동이 없습니다."
                        : tradeTapeFilter === "errors"
                          ? "오류·미체결 활동이 없습니다."
                          : "아직 표시할 체결·청산 활동이 없습니다."
                      : "거래 내역을 불러오는 중입니다."}
                  </p>
                )}
              </div>
            </section>
          </motion.section>
        )}

        {activeTab === "account" && (
          <motion.section
            key="account"
            className="tab-screen"
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.24 }}
          >
            <section className="panel account-panel solo-panel">
              <div className="panel-head">
                <div>
                  <p className="eyebrow">자산 세부</p>
                  <h2>현재 노출</h2>
                </div>
                <WalletCards size={21} />
              </div>
              <dl className="account-list">
                <div>
                  <dt>주소</dt>
                  <dd>{compactAddress(portfolio?.accountAddress)}</dd>
                </div>
                <div>
                  <dt>포지션</dt>
                  <dd>{positions.length}개</dd>
                </div>
                <div>
                  <dt>대기 주문</dt>
                  <dd>{openOrders.length}개</dd>
                </div>
                <div>
                  <dt>사용 증거금</dt>
                  <dd>{formatDollar(portfolio?.userState?.marginSummary?.totalMarginUsed)}</dd>
                </div>
              </dl>
              <div className="position-list">
                {positions.map((entry, index) => {
                  const coin = entry.position?.coin ?? "-";
                  const mark = sideMarkFromSize(entry.position?.szi);
                  return (
                    <article
                      className="position"
                      key={(entry.position?.coin ?? "position") + "-" + index}
                    >
                      <TokenMark
                        compact
                        symbol={coin}
                        imageUrl={tokenImageUrl(coin)}
                        sideMark={mark}
                      />
                      <strong>{coin}</strong>
                      <span>{formatNumber(entry.position?.szi)}</span>
                      <span>{formatSignedUsdc(entry.position?.unrealizedPnl)}</span>
                    </article>
                  );
                })}
                {!positions.length && <p className="empty">현재 열린 포지션이 없습니다.</p>}
              </div>
            </section>
          </motion.section>
        )}
      </AnimatePresence>
    </main>
  );
}
